import { Alert, Button, Flex, Select, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { CsvMatchyUpload } from '~/app/components/knowledgeBase/csvUpload/csvUploadMatchyLibrary/CsvMatchyUpload';
import { CsvPreviewTable } from '~/app/components/knowledgeBase/csvUpload/csvUploadMatchyLibrary/CsvPreviewTable';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';
import { assetImportCsvConfig } from '../../assetImportCsvConfig';
import { useAssetImportMatchy } from './useAssetImportMatchy';

const { Paragraph, Text, Title } = Typography;

export function AssetImportMatchyScreen() {
  const { t } = useTranslation('assets');
  const {
    assetModels,
    assetTypes,
    confirmationMessage,
    csvRows,
    currentSuggestions,
    error,
    fileName,
    handleConfirmMapping,
    handleFileLoaded,
    handleMappingChange,
    handleSubmitData,
    handleUploadData,
    isLoadingAssetModels,
    isLoadingAssetTypes,
    previewColumns,
    selectedAssetModelId,
    selectedAssetTypeId,
    setSelectedAssetModelId,
    setSelectedAssetTypeId,
    stagedUploadRows,
    status,
  } = useAssetImportMatchy();

  const assetTypeOptions = assetTypes.map((assetType) => ({
    label: assetType.name,
    value: assetType.id,
  }));

  const assetModelOptions = assetModels.map((assetModel) => ({
    label: assetModel.name,
    value: assetModel.id,
  }));

  return (
    <Space orientation="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={2} style={{ marginBottom: 8 }}>
          {t('importFlow.title', 'Asset import')}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t(
            'importFlow.description',
            'Upload a CSV or XLSX file, review the suggested mappings, then choose an asset type and asset model before uploading.',
          )}
        </Paragraph>
      </div>

      <Flex vertical gap={16}>
        <Alert
          message={t(
            'importFlow.requirementsTitle',
            'Asset import requirements',
          )}
          description={
            <Flex vertical gap={8}>
              <Text>
                {t(
                  'importFlow.requirementsDescription',
                  'Your file must include columns mappable to the system fields below.',
                )}
              </Text>
              <Flex wrap="wrap" gap={4}>
                {assetImportCsvConfig.columns.map((col) => (
                  <Tag key={col.key} color={col.required ? 'blue' : 'default'}>
                    {col.title} {col.required ? '*' : ''}
                  </Tag>
                ))}
              </Flex>
            </Flex>
          }
          type="info"
          showIcon
        />

        {fileName ? (
          <Text type="secondary">
            {t('importFlow.fileLabel', 'File')}: <Text code>{fileName}</Text>
          </Text>
        ) : null}

        {error ? <Alert type="error" title={error} showIcon /> : null}
        {confirmationMessage ? (
          <Alert type="success" title={confirmationMessage} showIcon />
        ) : null}

        <CsvMatchyUpload
          config={{ ...assetImportCsvConfig, fileName }}
          onFileLoadedAction={handleFileLoaded}
          onMappingChangeAction={handleMappingChange}
          onSubmitDataAction={handleSubmitData}
        />

        <CsvPreviewTable
          columns={previewColumns}
          data={csvRows}
          errors={error ? [error] : []}
          status={status}
        />

        {stagedUploadRows.length > 0 ? (
          <>
            <div>
              <Title level={5} style={{ marginBottom: 4 }}>
                {t('importFlow.selectionTitle', 'Import context')}
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {t(
                  'importFlow.selectionDescription',
                  'Select the asset type first, then choose one of its asset models before uploading the mapped file.',
                )}
              </Paragraph>
            </div>

            <FilterBar>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={t(
                  'importFlow.assetTypePlaceholder',
                  'Select an asset type',
                )}
                value={selectedAssetTypeId || undefined}
                options={assetTypeOptions}
                loading={isLoadingAssetTypes}
                onChange={setSelectedAssetTypeId}
              />
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={t(
                  'importFlow.assetModelPlaceholder',
                  'Select an asset model',
                )}
                value={selectedAssetModelId || undefined}
                options={assetModelOptions}
                loading={isLoadingAssetModels}
                disabled={
                  !selectedAssetTypeId || assetModelOptions.length === 0
                }
                onChange={setSelectedAssetModelId}
              />
              {!isLoadingAssetModels &&
              selectedAssetTypeId &&
              assetModelOptions.length === 0 ? (
                <Text type="secondary">
                  {t(
                    'importFlow.noAssetModels',
                    'No asset models are available for the selected asset type.',
                  )}
                </Text>
              ) : null}

              <div className="evimo-filter-bar__actions">
                <Button
                  type="default"
                  disabled={Object.keys(currentSuggestions).length === 0}
                  onClick={handleConfirmMapping}
                >
                  {t('importFlow.confirmMapping', 'Confirm mapping')}
                </Button>
                <Button
                  type="primary"
                  disabled={
                    stagedUploadRows.length === 0 ||
                    !selectedAssetTypeId ||
                    !selectedAssetModelId ||
                    isLoadingAssetTypes ||
                    isLoadingAssetModels ||
                    status === 'parsing'
                  }
                  onClick={handleUploadData}
                >
                  {t('importFlow.uploadButton', 'Upload mapped file')}
                </Button>
              </div>
            </FilterBar>
          </>
        ) : null}
      </Flex>
    </Space>
  );
}
